import { Space } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomField from '../../components/CustomField';
import { EndUserRequestFormValues } from '../../components/GrispiEndUserRequestForm';
import useFieldDefinitionListFromEndUserForm from '../../hooks/end-user-form/useFieldDefinitionListFromEndUserForm';
import { UIForm } from '../../hooks/ticketDetailPage/useAgentFormById';
import { Attributes, FieldRequirement, TicketSystemFieldsKey } from '../../model/types';
import { getFieldRuleByKey } from '../../utils/forms/formHelper.util';
import { getValidationRules } from '../../utils/forms/formHelper.validators';
import {
    ConditionController,
    ListenedConditionController,
    generateReactHookFormFriendlyFieldName
} from '../Pages/TicketPages/TicketPagesContentLayout/AgentFormActiveFieldsContainer';

export const shouldRenderForOptimisticUI = (fieldDefinitionKey: string) =>
    [
        TicketSystemFieldsKey.subject,
        TicketSystemFieldsKey.description,
        TicketSystemFieldsKey.attachments,
        TicketSystemFieldsKey.untrusted_end_user
    ].includes(fieldDefinitionKey as TicketSystemFieldsKey);

const EndUserFormActiveFieldsContainer = ({ form }: { form: UIForm }) => {
    const activeFields = useFieldDefinitionListFromEndUserForm();
    const { ready } = useTranslation('admin');

    return (
        <Space id="css-fix-ref" direction="vertical" className="full-width" size="small">
            {activeFields.map((fieldDefinition) => {
                if (!fieldDefinition || !ready) return null;

                const { attributes, key } = fieldDefinition;
                if (attributes.includes(Attributes.FIXED_PLACE_IN_UI) && !shouldRenderForOptimisticUI(key)) return null;

                const name =
                    fieldDefinition.key === TicketSystemFieldsKey.description
                        ? 'comment.body'
                        : generateReactHookFormFriendlyFieldName(fieldDefinition.key);

                const fieldRuleOnForm = form?.fields?.find(getFieldRuleByKey(fieldDefinition));
                const fieldDefinitionNotAllowNewValues = {
                    ...fieldDefinition,
                    attributes: attributes.filter((attr) => attr !== Attributes.ALLOW_NEW_VALUES)
                };
                return (
                    <Controller<EndUserRequestFormValues, any>
                        defaultValue={
                            fieldDefinition.key === TicketSystemFieldsKey.untrusted_end_user ? 'null:null:null' : null
                        }
                        key={fieldDefinition.key}
                        name={name}
                        render={({ field: { value, onChange }, fieldState: { error } }) => {
                            return (
                                <>
                                    {fieldRuleOnForm?.conditions && (
                                        <ConditionController fieldKey={key} conditions={fieldRuleOnForm.conditions} />
                                    )}
                                    <ListenedConditionController
                                        listenedConditionOnForm={fieldRuleOnForm?.listenedConditionOnForm}
                                    >
                                        <CustomField
                                            containerProps={{
                                                errorMessage: error?.message,
                                                fieldRequirement:
                                                    (fieldRuleOnForm?.listenedConditionOnForm?.action
                                                        ?.required as FieldRequirement) ||
                                                    fieldRuleOnForm?.required ||
                                                    fieldDefinition?.required
                                            }}
                                            fieldDefinition={fieldDefinitionNotAllowNewValues}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    </ListenedConditionController>
                                </>
                            );
                        }}
                        rules={{
                            validate: getValidationRules({
                                fieldRuleOnForm,
                                fieldDefinition
                            })
                        }}
                    />
                );
            })}
        </Space>
    );
};

export default EndUserFormActiveFieldsContainer;
